import { PlusOutlined } from '@ant-design/icons';
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Popconfirm, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
// Api
import { LIST_CHANNEL_MONETIZATION_PROGRAMS } from 'api/channelMonetization/queries';
import { BACKFILL_CHANNEL_MONETIZATION_CURRENT_MONTH } from 'api/channelMonetization/mutations';
// Types
import {
  ListChannelMonetizationPrograms,
  ListChannelMonetizationPrograms_listChannelMonetizationPrograms,
} from 'api/channelMonetization/types/ListChannelMonetizationPrograms';
import { BackfillChannelMonetizationCurrentMonth } from 'api/channelMonetization/types/BackfillChannelMonetizationCurrentMonth';
import { ChannelMonetizationProgramStatus } from 'api/graphql-global-types';
// Components
import ProgramActions from './components/ProgramActions';
import CreateEditProgramModal from './components/CreateEditProgramModal';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './ChannelMonetization.module.scss';

const statusClass = (status: ChannelMonetizationProgramStatus): string => {
  switch (status) {
    case ChannelMonetizationProgramStatus.Open:
      return styles.statusOpen;
    case ChannelMonetizationProgramStatus.Draft:
      return styles.statusDraft;
    default:
      return styles.statusClosed;
  }
};

const ChannelMonetization: React.FC = () => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedProgram, setSelectedProgram] =
    useState<ListChannelMonetizationPrograms_listChannelMonetizationPrograms | null>(
      null
    );

  const { data, loading, refetch } = useQuery<ListChannelMonetizationPrograms>(
    LIST_CHANNEL_MONETIZATION_PROGRAMS,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [backfill, { loading: backfilling }] =
    useMutation<BackfillChannelMonetizationCurrentMonth>(
      BACKFILL_CHANNEL_MONETIZATION_CURRENT_MONTH
    );

  const handleBackfill = async () => {
    try {
      const res = await backfill();
      const result = res.data?.backfillChannelMonetizationCurrentMonth;
      successNotification(
        `Backfill complete: ${result?.rebuilt ?? 0}/${
          result?.processed ?? 0
        } stores rebuilt`
      );
      refetch();
    } catch (error) {
      errorNotification((error as Error).message);
    }
  };

  const handleCreate = () => {
    setSelectedProgram(null);
    setIsEdit(false);
    setModalVisible(true);
  };

  const handleEdit = (
    program: ListChannelMonetizationPrograms_listChannelMonetizationPrograms
  ) => {
    setSelectedProgram(program);
    setIsEdit(true);
    setModalVisible(true);
  };

  const handleClose = () => {
    setModalVisible(false);
    setSelectedProgram(null);
    setIsEdit(false);
  };

  const columns = [
    {
      title: 'Month',
      dataIndex: 'monthLabel',
      key: 'monthLabel',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ChannelMonetizationProgramStatus) => (
        <span className={statusClass(status)}>{status}</span>
      ),
    },
    {
      title: 'Prize',
      key: 'prize',
      width: 160,
      render: (
        _: unknown,
        record: ListChannelMonetizationPrograms_listChannelMonetizationPrograms
      ) => `${record.prizeAmount} ${record.prizeCurrency}`,
    },
    {
      title: 'Slots',
      dataIndex: 'slotsPerMonth',
      key: 'slotsPerMonth',
      width: 100,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (createdAt: string) => new Date(createdAt).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (
        _: unknown,
        record: ListChannelMonetizationPrograms_listChannelMonetizationPrograms
      ) => (
        <ProgramActions
          program={record}
          onEdit={handleEdit}
          onRefetch={refetch}
        />
      ),
    },
  ];

  return (
    <>
      <div className={styles.root}>
        <PageToolbar>
          <Popconfirm
            title="Rebuild the leaderboard for all stores active this month? Run once after launch, or to repair drift."
            onConfirm={handleBackfill}
            okText="Run backfill"
            cancelText="Cancel"
          >
            <Button icon={<ReloadOutlined />} loading={backfilling}>
              Backfill This Month
            </Button>
          </Popconfirm>
          <Button icon={<PlusOutlined />} type="primary" onClick={handleCreate}>
            Create New Program
          </Button>
        </PageToolbar>

        <Table<ListChannelMonetizationPrograms_listChannelMonetizationPrograms>
          rowKey="id"
          columns={columns}
          dataSource={data?.listChannelMonetizationPrograms ?? []}
          loading={loading}
          pagination={false}
          scroll={{ x: 800 }}
        />
      </div>

      <CreateEditProgramModal
        isVisible={modalVisible}
        isEdit={isEdit}
        program={selectedProgram}
        onClose={handleClose}
        onSaved={refetch}
      />
    </>
  );
};

export default ChannelMonetization;
